// src/components/LanguageAssistant.tsx
import React, { useState, useRef, useEffect } from 'react';
import {
  Mic,
  Volume2,
  Globe,
  MessageSquare,
  Sparkles,
  X,
  Loader2,
  Send,
  BookOpen,
  Heart,
  Trophy,
} from 'lucide-react';
import { supabase } from '../supabaseClient';

interface LanguageAssistantProps {
  userId: string;
  preferredVoice?: string;     // provient de ClubForfeo
  ttsEnabled?: boolean;        // provient de ClubForfeo
}

interface ConversationMessage {
  role: 'user' | 'assistant';
  content: string;
  language?: string;
}

const LanguageAssistant = ({
  userId,
  preferredVoice = 'verse',
  ttsEnabled = true,
}: LanguageAssistantProps) => {
  const [selectedLanguage, setSelectedLanguage] = useState('français');
  const [userInput, setUserInput] = useState('');
  const [conversation, setConversation] = useState<ConversationMessage[]>([
    {
      role: 'assistant',
      content:
        'Woof! Bonjour ! 🐾 Je suis Forfy, ton assistant personnel pour apprendre les langues en voyageant. Choisis une langue et commençons notre aventure !',
    },
  ]);

  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [pointsEarned, setPointsEarned] = useState(0);
  const [audioUnlocked, setAudioUnlocked] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const conversationEndRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<any>(null);
  const abortSpeakRef = useRef<AbortController | null>(null);

  // ✅ Audio element ref (stop + reuse)
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioUrlRef = useRef<string | null>(null);

  const languages = [
    { code: 'français', name: 'Français', flag: '🇫🇷', color: 'from-blue-500 to-blue-700' },
    { code: 'anglais', name: 'Anglais', flag: '🇺🇸', color: 'from-red-500 to-blue-600' },
    { code: 'espagnol', name: 'Espagnol', flag: '🇪🇸', color: 'from-yellow-500 to-red-600' },
    { code: 'allemand', name: 'Allemand', flag: '🇩🇪', color: 'from-black to-yellow-500' },
    { code: 'chinois', name: 'Chinois', flag: '🇨🇳', color: 'from-red-500 to-yellow-500' },
    { code: 'mandarin', name: 'Mandarin', flag: '🇨🇳', color: 'from-red-600 to-orange-500' },
    { code: 'russe', name: 'Russe', flag: '🇷🇺', color: 'from-blue-600 to-red-600' },
  ];

  const essentialPhrases: Record<string, string[]> = {
    français: [
      'Bonjour, comment ça va ?',
      "Je voudrais un café s'il vous plaît",
      'Où est la gare ?',
      'Combien ça coûte ?',
      'Parlez-vous anglais ?',
      'Je cherche un hôtel',
      'Merci beaucoup',
      'Au revoir',
    ],
    anglais: [
      'Hello, how are you?',
      'I would like a coffee please',
      'Where is the train station?',
      'How much does it cost?',
      'Do you speak English?',
      "I'm looking for a hotel",
      'Thank you very much',
      'Goodbye',
    ],
    espagnol: [
      'Hola, ¿cómo estás?',
      'Me gustaría un café por favor',
      '¿Dónde está la estación de tren?',
      '¿Cuánto cuesta?',
      '¿Hablas inglés?',
      'Estoy buscando un hotel',
      'Muchas gracias',
      'Adiós',
    ],
    allemand: [
      "Hallo, wie geht's?",
      'Ich hätte gerne einen Kaffee bitte',
      'Wo ist der Bahnhof?',
      'Wie viel kostet das?',
      'Sprechen Sie Englisch?',
      'Ich suche ein Hotel',
      'Vielen Dank',
      'Auf Wiedersehen',
    ],
    chinois: ['你好，你好吗？', '我想要一杯咖啡，请', '火车站在哪里？', '这个多少钱？', '你会说英语吗？', '我在找一家酒店', '非常感谢', '再见'],
    mandarin: ['你好，你好吗？', '我想要一杯咖啡，请', '火车站在哪里？', '这个多少钱？', '你会说英语吗？', '我在找一家酒店', '非常感谢', '再见'],
    russe: [
      'Привет, как дела?',
      'Я хотел бы кофе, пожалуйста',
      'Где вокзал?',
      'Сколько это стоит?',
      'Вы говорите по-английски?',
      'Я ищу отель',
      'Большое спасибо',
      'До свидания',
    ],
  };

  const culturalTips: Record<string, string> = {
    français:
      "En France, dire 'Bonjour' en entrant dans un magasin est essentiel. On fait généralement la bise pour se saluer entre amis.",
    anglais:
      "Au Royaume-Uni, dire 'please' et 'thank you' est très important. Évitez de parler trop fort dans les transports en commun.",
    espagnol:
      "En Espagne, le dîner est tard (21h-23h). On dit souvent '¡Hola!' avec enthousiasme pour saluer.",
    allemand:
      "En Allemagne, on se serre la main fermement en regardant dans les yeux. La ponctualité est très importante.",
    chinois:
      "En Chine, offrez et recevez avec les deux mains. Évitez le chiffre 4 (considéré comme malchanceux).",
    mandarin: "À Taïwan, hocher la tête signifie 'oui'. Le respect des aînés est très important.",
    russe:
      'En Russie, souriez moins aux inconnus (cela peut être perçu comme de l\'immaturité). Portez des pantoufles à l\'intérieur.',
  };

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch((e) => console.log('Auto-play prevented:', e));
    }
  }, []);

  useEffect(() => {
    conversationEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation, isProcessing]);

  // Cleanup TTS on unmount
  useEffect(() => {
    return () => {
      try {
        if (recognitionRef.current) recognitionRef.current.abort?.();
      } catch {}
      try {
        window.speechSynthesis?.cancel();
      } catch {}
      try {
        abortSpeakRef.current?.abort();
      } catch {}
      try {
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.src = '';
        }
      } catch {}
      try {
        if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
      } catch {}
    };
  }, []);

  const getLanguageCode = (language: string) => {
    const codes: Record<string, string> = {
      français: 'fr-FR',
      anglais: 'en-US',
      espagnol: 'es-ES',
      allemand: 'de-DE',
      chinois: 'zh-CN',
      mandarin: 'zh-CN',
      russe: 'ru-RU',
    };
    return codes[language] || 'fr-FR';
  };

  // ✅ iOS/Safari: unlock audio on user gesture
  const unlockAudio = async () => {
    if (audioUnlocked) return;
    try {
      // Resume AudioContext if available
      const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (AudioCtx) {
        const ctx = new AudioCtx();
        await ctx.resume();
        await ctx.close?.();
      }

      // Also try a silent play (works on some iOS versions)
      const silent = new Audio(
        'data:audio/mp3;base64,//uQZAAAAAAAAAAAAAAAAAAAAAAASW5mbwAAAA8AAAACAAACcQCA' // micro-silence
      );
      silent.volume = 0;
      await silent.play().catch(() => {});
      silent.pause();

      setAudioUnlocked(true);
    } catch {
      // Even if it fails, we keep going
      setAudioUnlocked(true);
    }
  };

  const initSpeechRecognition = () => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition =
        (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;

      const recognition = new SpeechRecognition();
      recognition.lang = getLanguageCode(selectedLanguage);
      recognition.interimResults = false;
      recognition.maxAlternatives = 1;
      recognition.continuous = false;

      recognition.onstart = () => {
        setIsListening(true);
        if (videoRef.current) videoRef.current.playbackRate = 1.5;
      };

      recognition.onresult = (event: any) => {
        const transcript = event.results?.[0]?.[0]?.transcript || '';
        if (!transcript) return;
        setUserInput(transcript);
        handleSendMessage(transcript);
      };

      recognition.onend = () => {
        setIsListening(false);
        if (videoRef.current) videoRef.current.playbackRate = 1;
      };

      recognition.onerror = (event: any) => {
        console.error('Erreur reconnaissance vocale:', event?.error);
        setIsListening(false);
        if (videoRef.current) videoRef.current.playbackRate = 1;

        // not-allowed => permissions mic
        // On laisse l'UI vivre, mais on peut guider l'utilisateur via alert si tu veux
      };

      recognitionRef.current = recognition;
    }
  };

  const startListening = async () => {
    await unlockAudio();

    if (!recognitionRef.current) initSpeechRecognition();

    if (recognitionRef.current) {
      try {
        recognitionRef.current.lang = getLanguageCode(selectedLanguage);
        recognitionRef.current.start();
      } catch (error) {
        console.error('Erreur démarrage reconnaissance:', error);
        alert("Impossible d'accéder au microphone. Vérifiez les permissions de votre navigateur.");
      }
    } else {
      alert("La reconnaissance vocale n'est pas supportée par votre navigateur. Essayez Chrome ou Edge.");
    }
  };

  const base64ToBlob = (base64: string, mime = 'audio/mpeg') => {
    const byteChars = atob(base64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteNumbers[i] = byteChars.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mime });
  };

  /**
   * OpenAI HD TTS via Supabase Edge Function 'ai-tts'
   * Fallback: browser speechSynthesis si ai-tts échoue OU si ttsEnabled=false.
   */
  const speakText = async (text: string) => {
    if (!ttsEnabled) return;

    await unlockAudio();

    // Stop anything currently playing
    try {
      window.speechSynthesis?.cancel();
    } catch {}
    try {
      abortSpeakRef.current?.abort();
    } catch {}

    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
        audioUrlRef.current = null;
      }
    } catch {}

    const controller = new AbortController();
    abortSpeakRef.current = controller;

    try {
      setIsSpeaking(true);
      if (videoRef.current) videoRef.current.playbackRate = 1.3;

      const { data, error } = await supabase.functions.invoke('ai-tts', {
        body: {
          text,
          voice: preferredVoice,
        },
      });

      if (error) throw error;

      const audioBase64: string | undefined = data?.audio_base64 || data?.audio;
      const audioUrl: string | undefined = data?.audioUrl;

      if (audioUrl) {
        const audio = new Audio(audioUrl);
        audioRef.current = audio;

        audio.onended = () => {
          setIsSpeaking(false);
          if (videoRef.current) videoRef.current.playbackRate = 1;
        };
        audio.onerror = () => {
          setIsSpeaking(false);
          if (videoRef.current) videoRef.current.playbackRate = 1;
        };

        if (!controller.signal.aborted) {
          await audio.play();
        }
        return;
      }

      if (audioBase64) {
        const blob = base64ToBlob(audioBase64, 'audio/mpeg');
        const url = URL.createObjectURL(blob);
        audioUrlRef.current = url;

        const audio = new Audio(url);
        audioRef.current = audio;

        audio.onended = () => {
          setIsSpeaking(false);
          if (videoRef.current) videoRef.current.playbackRate = 1;
          try {
            if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
          } catch {}
          audioUrlRef.current = null;
        };
        audio.onerror = () => {
          setIsSpeaking(false);
          if (videoRef.current) videoRef.current.playbackRate = 1;
          try {
            if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
          } catch {}
          audioUrlRef.current = null;
        };

        if (!controller.signal.aborted) {
          await audio.play();
        }
        return;
      }

      throw new Error('No audio returned from ai-tts');
    } catch (e) {
      // Fallback navigateur
      try {
        if ('speechSynthesis' in window) {
          const speech = new SpeechSynthesisUtterance(text);
          speech.lang = getLanguageCode(selectedLanguage);
          speech.rate = 0.9;
          speech.pitch = 1.1;
          speech.volume = 1;

          speech.onstart = () => {
            setIsSpeaking(true);
            if (videoRef.current) videoRef.current.playbackRate = 1.3;
          };

          speech.onend = () => {
            setIsSpeaking(false);
            if (videoRef.current) videoRef.current.playbackRate = 1;
          };

          speech.onerror = () => {
            setIsSpeaking(false);
            if (videoRef.current) videoRef.current.playbackRate = 1;
          };

          window.speechSynthesis.cancel();
          window.speechSynthesis.speak(speech);
        } else {
          setIsSpeaking(false);
          if (videoRef.current) videoRef.current.playbackRate = 1;
        }
      } catch (err2) {
        console.error('TTS fallback error:', err2);
        setIsSpeaking(false);
        if (videoRef.current) videoRef.current.playbackRate = 1;
      }
    }
  };

  const getAIResponse = async (message: string): Promise<string> => {
    setIsProcessing(true);

    try {
      const { data, error } = await supabase.functions.invoke('ai-language-tutor', {
        body: {
          message,
          target_language: selectedLanguage,
          user_id: userId,
          conversation_history: conversation.slice(-5),
        },
      });

      if (error) {
        console.error('Erreur API:', error);
        const fallbackResponses: Record<string, string> = {
          français: `Woof! 🐾 Excellente question ! En français, on dirait: '${message}'. Continue à pratiquer !`,
          anglais: `Woof! 🐾 Great question! In English, you would say: '${message}'. Keep practicing!`,
          espagnol: `¡Guau! 🐾 ¡Excelente pregunta! En español, dirías: '${message}'. ¡Sigue practicando!`,
          allemand: `Wuff! 🐾 Ausgezeichnete Frage! Auf Deutsch würde man sagen: '${message}'. Weiter so!`,
          chinois: `汪汪！🐾 很好的问题！用中文说：'${message}'。继续练习！`,
          mandarin: `汪汪！🐾 很好的问题！用中文说：'${message}'。继续练习！`,
          russe: `Гав! 🐾 Отличный вопрос! По-русски вы бы сказали: '${message}'. Продолжайте практиковаться!`,
        };
        return fallbackResponses[selectedLanguage] || "Woof! 🐾 Désolé, j'ai un petit problème technique. Réessaye dans un instant !";
      }

      return data?.response || "Woof! 🐾 Je ne sais pas quoi répondre pour le moment. Essaye une autre question !";
    } catch (error) {
      console.error('Erreur:', error);
      return "Woof! 🐾 Désolé, j'ai un petit problème technique. Réessaye dans un instant !";
    } finally {
      setIsProcessing(false);
    }
  };

  const awardLanguagePoints = async (points: number, action: string) => {
    if (!userId) return;

    try {
      const { error } = await supabase.rpc('add_points', {
        p_user_id: userId,
        p_amount: points,
        p_action: action,
        p_desc: `Apprentissage ${selectedLanguage}`,
      });

      if (!error) setPointsEarned((prev) => prev + points);
    } catch (e) {
      console.error('Erreur attribution points:', e);
    }
  };

  const handleSendMessage = async (message = userInput) => {
    if (!message.trim()) return;

    await unlockAudio();

    const userMessage: ConversationMessage = {
      role: 'user',
      content: message,
      language: selectedLanguage,
    };

    setConversation((prev) => [...prev, userMessage]);
    setUserInput('');

    await awardLanguagePoints(5, 'SPEAK_LANGUAGE');

    const assistantResponse = await getAIResponse(message);

    const assistantMessage: ConversationMessage = {
      role: 'assistant',
      content: assistantResponse,
      language: selectedLanguage,
    };

    setConversation((prev) => [...prev, assistantMessage]);

    await awardLanguagePoints(3, 'RECEIVE_FEEDBACK');

    // ✅ Sur iOS, setTimeout après réseau peut être bloqué.
    // On tente direct; si bloqué, l'utilisateur peut cliquer le bouton "Écouter".
    if (ttsEnabled) {
      speakText(assistantResponse).catch(() => {});
    }
  };

  const handleEssentialPhrase = async (phrase: string) => {
    await unlockAudio();

    const userMessage: ConversationMessage = {
      role: 'user',
      content: `Comment dit-on : "${phrase}" ?`,
      language: selectedLanguage,
    };

    const assistantMessage: ConversationMessage = {
      role: 'assistant',
      content: `Parfait ! En ${selectedLanguage}, on dit : "${phrase}". Répète après moi ! 🐾`,
      language: selectedLanguage,
    };

    setConversation((prev) => [...prev, userMessage, assistantMessage]);

    speakText(phrase).catch(() => {});
    awardLanguagePoints(10, 'LEARN_PHRASE');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const getCurrentPhrases = () => essentialPhrases[selectedLanguage] || essentialPhrases.français;
  const getCurrentCulturalTip = () => culturalTips[selectedLanguage] || culturalTips.français;

  const clearConversation = () => {
    setConversation([
      {
        role: 'assistant',
        content:
          "Woof! 🐾 Conversation réinitialisée ! Choisis une langue et commençons une nouvelle aventure linguistique !",
      },
    ]);
  };

  return (
    <div className="flex flex-col lg:flex-row gap-8">
      {/* Section gauche */}
      <div className="lg:w-2/5 space-y-6">
        <div className="bg-gradient-to-br from-purple-100 to-pink-100 rounded-3xl p-6 border-2 border-purple-200 shadow-xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-black text-purple-900 text-xl flex items-center gap-2">
              <Sparkles className="text-purple-600" />
              Forfy Assistant
            </h3>
            <div className="flex items-center gap-2">
              <div
                className={`w-3 h-3 rounded-full ${
                  isSpeaking ? 'bg-green-500 animate-pulse' : isListening ? 'bg-blue-500 animate-pulse' : 'bg-gray-300'
                }`}
              />
              <span className="text-xs font-bold text-purple-700">
                {isSpeaking ? 'Parle...' : isListening ? 'Écoute...' : 'Prêt'}
              </span>
            </div>
          </div>

          <div className="relative rounded-2xl overflow-hidden bg-black mb-6 border-4 border-white shadow-lg">
            <video
              ref={videoRef}
              src="/GifForfy.MP4"
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-64 object-cover"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
              <p className="text-white text-sm font-bold text-center">Forfy • Pomeranien • Expert en langues</p>
            </div>
          </div>

          <div className="bg-gradient-to-r from-yellow-50 to-amber-100 p-4 rounded-2xl mb-6 border border-yellow-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Trophy className="text-yellow-600" size={20} />
                <span className="text-sm font-bold text-yellow-800">Points gagnés</span>
              </div>
              <span className="text-2xl font-black text-yellow-700">{pointsEarned}</span>
            </div>
            <p className="text-xs text-yellow-600 mt-2">Gagne 5 points par phrase parlée, 10 points par nouvelle phrase apprise !</p>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-black text-purple-900 mb-3 uppercase tracking-widest flex items-center gap-2">
              <Globe size={14} />
              Langue à pratiquer
            </label>
            <div className="grid grid-cols-4 gap-2">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => setSelectedLanguage(lang.code)}
                  className={`p-3 rounded-xl border-2 transition-all transform hover:scale-105 ${
                    selectedLanguage === lang.code
                      ? 'border-purple-600 bg-white shadow-lg scale-105'
                      : 'border-purple-200 bg-white/50 hover:border-purple-300'
                  }`}
                >
                  <div className="text-2xl mb-1">{lang.flag}</div>
                  <div className="text-xs font-bold text-purple-900 truncate">{lang.name}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-cyan-100 p-4 rounded-2xl border border-blue-200">
            <div className="flex items-center gap-2 mb-2">
              <BookOpen className="text-blue-600" size={16} />
              <span className="text-sm font-bold text-blue-800">Conseil culturel</span>
            </div>
            <p className="text-xs text-blue-700">{getCurrentCulturalTip()}</p>
          </div>

          {/* ✅ Info unlock (discret) */}
          <div className="mt-4 text-[10px] text-purple-600 font-bold text-center">
            Audio: {ttsEnabled ? (audioUnlocked ? 'prêt ✅' : 'clique un bouton pour activer 🔊') : 'désactivé'}
          </div>
        </div>

        <div className="bg-white rounded-3xl p-6 border-2 border-emerald-200 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-black text-emerald-900 text-lg flex items-center gap-2">
              <MessageSquare size={16} />
              Phrases essentielles
            </h4>
            <span className="text-xs font-bold bg-emerald-100 text-emerald-700 px-2 py-1 rounded">Voyage</span>
          </div>

          <div className="space-y-2 max-h-60 overflow-y-auto pr-2">
            {getCurrentPhrases().slice(0, 8).map((phrase, idx) => (
              <button
                key={idx}
                onClick={() => handleEssentialPhrase(phrase)}
                className="w-full text-left p-3 bg-emerald-50 hover:bg-emerald-100 rounded-xl border border-emerald-200 transition-all transform hover:scale-[1.02] group"
              >
                <div className="flex items-start gap-2">
                  <div className="bg-emerald-100 text-emerald-600 p-1.5 rounded-lg group-hover:bg-emerald-200 transition">
                    <Volume2 size={12} />
                  </div>
                  <span className="text-sm text-emerald-800 font-medium flex-1">{phrase}</span>
                  <Heart size={14} className="text-emerald-300 group-hover:text-emerald-500 transition" />
                </div>
              </button>
            ))}
          </div>

          <div className="mt-4 pt-4 border-t border-emerald-100">
            <p className="text-xs text-emerald-600 text-center">Clique sur une phrase pour l'entendre et la pratiquer !</p>
          </div>
        </div>
      </div>

      {/* Section droite */}
      <div className="lg:w-3/5 flex flex-col">
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-3xl p-6 text-white mb-6 shadow-xl">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h2 className="font-black text-2xl mb-2">Apprends avec Forfy</h2>
              <p className="text-purple-100 text-sm">
                Pose tes questions, parle dans le micro, répète après moi. Ensemble, maîtrisons le {selectedLanguage} ! 🐾
              </p>
              <p className="text-white/80 text-xs mt-2 font-bold">
                Voix: <span className="text-white">{preferredVoice}</span> • {ttsEnabled ? 'TTS HD activé' : 'TTS désactivé'}
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={clearConversation}
                className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-xl text-sm font-bold transition flex items-center gap-2"
              >
                <X size={16} />
                Nouvelle discussion
              </button>
              <div className="text-right">
                <div className="text-[10px] font-bold text-slate-100/80 uppercase tracking-widest mb-1">Statut</div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                  <span className="text-sm font-bold">En ligne</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 bg-white rounded-3xl border-2 border-slate-200 shadow-lg overflow-hidden flex flex-col mb-6">
          <div className="bg-slate-50 p-4 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center text-white">
                  <span className="text-lg">🐾</span>
                </div>
                <div>
                  <h3 className="font-black text-slate-900">Chat avec Forfy</h3>
                  <p className="text-xs text-slate-500">Pratique ton {selectedLanguage} en temps réel</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Mode</div>
                <div className="text-xs font-bold text-purple-600">Apprentissage Actif</div>
              </div>
            </div>
          </div>

          <div className="flex-1 p-6 overflow-y-auto max-h-[400px] bg-gradient-to-b from-white to-slate-50">
            <div className="space-y-4">
              {conversation.map((msg, idx) => (
                <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[85%] rounded-2xl p-4 relative ${
                      msg.role === 'user'
                        ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-br-none'
                        : 'bg-gradient-to-r from-purple-100 to-pink-100 text-purple-900 rounded-bl-none border border-purple-200'
                    }`}
                  >
                    {msg.role === 'user' && msg.language && (
                      <div className="absolute -top-2 -left-2 bg-white text-blue-600 text-[10px] font-black px-2 py-0.5 rounded-full border border-blue-200">
                        {msg.language}
                      </div>
                    )}

                    <div className="flex items-center gap-2 mb-2">
                      {msg.role === 'assistant' && (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                          <span className="text-sm">🐶</span>
                        </div>
                      )}
                      <span className={`text-xs font-bold ${msg.role === 'user' ? 'text-blue-100' : 'text-purple-600'}`}>
                        {msg.role === 'user' ? 'Vous' : 'Forfy'}
                      </span>
                    </div>

                    <p className="text-sm leading-relaxed whitespace-pre-line">{msg.content}</p>

                    {msg.role === 'assistant' && (
                      <div className="mt-3 flex justify-end">
                        <button
                          onClick={() => speakText(msg.content)}
                          disabled={isSpeaking || !ttsEnabled}
                          className="p-1.5 bg-white text-purple-600 rounded-lg hover:bg-purple-50 transition disabled:opacity-50"
                          title={ttsEnabled ? 'Écouter' : 'TTS désactivé'}
                        >
                          <Volume2 size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {isProcessing && (
                <div className="flex justify-start">
                  <div className="max-w-[85%] rounded-2xl p-4 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-900 rounded-bl-none border border-purple-200">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                        <span className="text-sm">🐶</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Loader2 size={16} className="animate-spin text-purple-600" />
                        <span className="text-sm font-medium">Forfy réfléchit...</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div ref={conversationEndRef} />
            </div>
          </div>

          <div className="border-t border-slate-200 p-6 bg-white">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${isListening ? 'bg-blue-500 animate-pulse' : 'bg-green-500'}`} />
                <span className="text-sm font-bold text-slate-700">
                  Mode : <span className="text-purple-600">{selectedLanguage}</span>
                </span>
              </div>
              <div className="text-xs text-slate-500 font-medium">{conversation.length - 1} échanges</div>
            </div>

            <div className="flex gap-3 mb-4">
              <button
                onClick={startListening}
                disabled={isListening || isSpeaking}
                className={`flex-1 py-4 rounded-2xl transition-all transform hover:scale-[1.02] ${
                  isListening
                    ? 'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg'
                    : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:shadow-xl'
                }`}
              >
                <div className="flex items-center justify-center gap-3">
                  <Mic size={20} />
                  <span className="font-bold">{isListening ? 'Écoute en cours...' : 'Parler au micro'}</span>
                </div>
                <div className="text-xs mt-1 opacity-90">{isListening ? 'Parle maintenant...' : 'Clique et parle en ' + selectedLanguage}</div>
              </button>

              <button
                onClick={() => {
                  const last = conversation[conversation.length - 1];
                  if (last && last.role === 'assistant') speakText(last.content);
                }}
                disabled={conversation.length === 0 || isSpeaking || conversation[conversation.length - 1]?.role === 'user' || !ttsEnabled}
                className="px-6 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-2xl transition-all transform hover:scale-105 disabled:opacity-50"
                title="Répéter le dernier message"
              >
                <div className="flex flex-col items-center justify-center h-full">
                  <Volume2 size={20} />
                  <span className="text-xs mt-1">Écouter</span>
                </div>
              </button>
            </div>

            <div className="relative">
              <textarea
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                onKeyDown={handleKeyPress as any}
                placeholder={`Écris ta question en ${selectedLanguage} ou utilise le micro ci-dessus... (ex: "Comment dit-on bonjour en ${selectedLanguage} ?")`}
                className="w-full h-32 p-4 border-2 border-slate-300 rounded-2xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition resize-none text-slate-700 pr-24"
                maxLength={500}
              />
              <div className="absolute bottom-4 right-4 flex flex-col gap-2">
                <button
                  onClick={() => handleSendMessage()}
                  disabled={!userInput.trim() || isProcessing}
                  className="p-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl hover:shadow-lg transition-all disabled:opacity-50"
                >
                  <Send size={18} />
                </button>
                <div className="text-[10px] text-slate-400 text-center">{userInput.length}/500</div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-3 rounded-xl border border-blue-100">
                <div className="text-xs font-bold text-blue-700 mb-1">💡 Astuce</div>
                <p className="text-xs text-blue-600">Parle lentement et clairement pour de meilleurs résultats</p>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-3 rounded-xl border border-purple-100">
                <div className="text-xs font-bold text-purple-700 mb-1">🎯 Objectif</div>
                <p className="text-xs text-purple-600">10 phrases par jour = 300 phrases par mois !</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-blue-50 to-white p-4 rounded-2xl border-2 border-blue-100 text-center">
            <div className="text-2xl font-black text-blue-600 mb-1">
              {conversation.filter((m) => m.role === 'user').length}
            </div>
            <div className="text-xs font-bold text-blue-600 uppercase tracking-widest">Phrases</div>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-white p-4 rounded-2xl border-2 border-purple-100 text-center">
            <div className="text-2xl font-black text-purple-600 mb-1">{Math.floor((conversation.length - 1) * 2.5)}</div>
            <div className="text-xs font-bold text-purple-600 uppercase tracking-widest">Minutes</div>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-white p-4 rounded-2xl border-2 border-green-100 text-center">
            <div className="text-2xl font-black text-green-600 mb-1">{pointsEarned}</div>
            <div className="text-xs font-bold text-green-600 uppercase tracking-widest">Points</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LanguageAssistant;
